import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import {
  useAlertConfigs,
  useDeleteAlertConfig,
  useUpdateAlertConfig,
} from './hooks/useAlertConfigs';
import { useAors } from '../../hooks/useAors';
import { useDistributionLists } from '../../hooks/useDistributionLists';
import { useAlertCategories } from './hooks/useAlertCategories';
import { selectGroupGuid } from '../../slices/commonSelectors';
import { DGPTAlertConfig, DGPTAlertCategory } from '../../types/dgptAlerts';
import { StylishNewButton } from '../../components/DesignSystems/New/StylishNewButton';
import StylishNewTable from '../../components/DesignSystems/New/StylishNewTable';
import { getRefreshCadenceLabel } from './utils/cronUtils';
import { TableColumn } from './types';

// Helper function to format date/time or return 'Never'
const formatLastRun = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  try {
    return format(new Date(dateString), 'Pp'); // e.g., 09/15/2023, 3:30 PM
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

const AlertConfigTable: React.FC = () => {
  const { data: configs, isLoading } = useAlertConfigs();
  const navigate = useNavigate();
  const groupGuid = useSelector(selectGroupGuid);

  const { data: allAor = [] } = useAors();
  const { data: teams = [], isLoading: isLoadingTeams } =
    useDistributionLists(groupGuid);
  const { data: categories, isLoading: isLoadingCategories } =
    useAlertCategories();
  const deleteConfig = useDeleteAlertConfig();
  const updateConfig = useUpdateAlertConfig();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [configToDelete, setConfigToDelete] = useState<DGPTAlertConfig | null>(
    null
  );

  const handleDelete = (config: DGPTAlertConfig): void => {
    setConfigToDelete(config);
    setShowDeleteModal(true);
  };

  const confirmDelete = (): void => {
    if (configToDelete) {
      deleteConfig.mutate(configToDelete.id);
      setShowDeleteModal(false);
      setConfigToDelete(null);
    }
  };

  if (isLoading || isLoadingTeams || isLoadingCategories) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const filteredConfigs = configs || [];
  const hasConfigs = filteredConfigs.length > 0;
  const hasTeams = teams?.length > 0;
  const hasAors = allAor?.length || 0 > 0;

  // Check if any categories have empty teams
  const hasCategoriesWithoutTeams =
    categories?.some(
      (category: DGPTAlertCategory) =>
        !category.teams || category.teams.length === 0
    ) || false;

  const columns: TableColumn[] = [
    {
      dataField: 'name',
      text: 'Name',
      sort: true,
      formatter: (cell: string, row: DGPTAlertConfig) => (
        <button
          className="btn btn-link p-0 text-start"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/aialerts/${row.id}`);
          }}
          style={{ textDecoration: 'none', fontWeight: 'normal' }}
        >
          {cell}
        </button>
      ),
    },
    {
      dataField: 'eventType',
      text: 'Event Type',
      sort: true,
      formatter: (cell: string) => cell || '-',
    },
    {
      dataField: 'aorName',
      text: 'AOR',
      sort: true,
      formatter: (cell: string) => cell || '-',
    },
    {
      dataField: 'refreshCadence',
      text: 'Refresh',
      sort: true,
      formatter: (cell: string) => getRefreshCadenceLabel(cell) || '-',
    },
    {
      dataField: 'lastRunAt',
      text: 'Last Run',
      sort: true,
      formatter: (cell: string) => formatLastRun(cell),
    },
    {
      dataField: 'editAction',
      text: 'Edit',
      sort: false,
      formatter: (_: unknown, row: DGPTAlertConfig) => (
        <button
          className="btn btn-link p-0"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/aialerts/${row.id}/edit`);
          }}
        >
          Edit
        </button>
      ),
    },
    {
      dataField: 'toggleActiveAction',
      text: 'Activate/Deactivate',
      sort: false,
      formatter: (_: unknown, row: DGPTAlertConfig) => (
        <button
          className="btn btn-link p-0"
          onClick={(e) => {
            e.stopPropagation();
            updateConfig.mutate({
              id: row.id,
              active: !row.active,
            });
          }}
          disabled={updateConfig.isPending}
        >
          {row.active ? 'Deactivate' : 'Activate'}
        </button>
      ),
    },
    {
      dataField: 'deleteAction',
      text: 'Delete',
      sort: false,
      formatter: (_: unknown, row: DGPTAlertConfig) => (
        <button
          className="btn btn-link p-0 text-danger"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(row);
          }}
          disabled={deleteConfig.isPending}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Alert Configurations</h5>
          <StylishNewButton
            className="button--primary"
            onClick={() => navigate('/aialerts/create')}
            disabled={!hasAors || !hasTeams || hasCategoriesWithoutTeams}
          >
            Create Alert Configuration
          </StylishNewButton>
        </div>

        {!hasAors && (
          <Alert variant="warning" className="mb-3">
            <strong>
              At least one <Link to="/groups/aor">Area of Responsibility</Link>{' '}
              is required for AI Alerts.
            </strong>
          </Alert>
        )}

        {!hasTeams && (
          <Alert variant="warning" className="mb-3">
            <strong>
              At least one <Link to="/groups/teams">Team</Link> is required for
              Alert Categories.
            </strong>
          </Alert>
        )}

        {hasCategoriesWithoutTeams && (
          <Alert variant="warning" className="mb-3">
            <strong>
              All <Link to="/groups/alert-categories">Alert Categories</Link>{' '}
              must be associated with at least one team to use AI Alerts.
            </strong>
          </Alert>
        )}

        {hasConfigs && (
          <StylishNewTable
            keyField="id"
            columns={columns}
            rows={filteredConfigs}
            noDataIndication="No alert configurations found"
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this alert configuration? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            className="button--secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton className="button--danger" onClick={confirmDelete}>
            Delete
          </StylishNewButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AlertConfigTable;
